import { createContext, forwardRef, useContext, useMemo } from 'react'
import type { JSX } from 'react'
import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  ReactNode,
} from 'react'

import { clsx } from 'clsx'

import {
  Button,
  CircleCheckBigIcon,
  Heading,
  IconButton,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'

import { Card } from '../../Card/Card'
import { BUFFER_HOME_TASK_STATUS } from '../useBufferHomeChecklist'
import type { BufferHomeTaskStatus } from '../useBufferHomeChecklist'

import styles from './TaskCard.module.css'

/** UI status for a Buffer Home first-steps task — the checklist task status,
 * kept in sync with the schema via the `GetBufferHomeChecklist` selection. */
export type TaskCardStatus = BufferHomeTaskStatus

interface TaskCardProps {
  status?: BufferHomeTaskStatus
  children: ReactNode
}

interface TaskCardSlotProps {
  children: ReactNode
}

type TaskCardActionVariant = 'primary' | 'tertiary'

type TaskCardActionProps = ComponentPropsWithRef<'button'> & {
  variant?: TaskCardActionVariant
}

type TaskCardContextValue = {
  status: BufferHomeTaskStatus
}

const TaskCardContext = createContext<TaskCardContextValue | null>(null)

function useTaskCardContext(component: string): TaskCardContextValue {
  const context = useContext(TaskCardContext)
  if (context === null) {
    throw new Error(`${component} must be rendered inside a <TaskCard>`)
  }
  return context
}

function TaskCardRoot({
  status = BUFFER_HOME_TASK_STATUS.Pending,
  children,
}: TaskCardProps): JSX.Element {
  const isDone =
    status === BUFFER_HOME_TASK_STATUS.Completed ||
    status === BUFFER_HOME_TASK_STATUS.Dismissed
  const contextValue = useMemo(() => ({ status }), [status])

  return (
    <TaskCardContext.Provider value={contextValue}>
      <Card
        radius="lg"
        className={clsx(styles.card, isDone && styles.cardDone)}
        data-status={status}
        data-testid="task-card"
      >
        {children}
      </Card>
    </TaskCardContext.Provider>
  )
}

TaskCardRoot.displayName = 'TaskCard'

function TaskCardHeader({ children }: TaskCardSlotProps): JSX.Element {
  return <div className={styles.header}>{children}</div>
}

TaskCardHeader.displayName = 'TaskCard.Header'

function TaskCardIcon({ children }: TaskCardSlotProps): JSX.Element {
  return (
    <span className={styles.iconWrapper} aria-hidden="true">
      {children}
    </span>
  )
}

TaskCardIcon.displayName = 'TaskCard.Icon'

function TaskCardTitle({ children }: TaskCardSlotProps): JSX.Element {
  return (
    <Heading as="h3" size="xsmall" className={styles.title}>
      {children}
    </Heading>
  )
}

TaskCardTitle.displayName = 'TaskCard.Title'

function TaskCardDescription({ children }: TaskCardSlotProps): JSX.Element {
  return <Text className={styles.description}>{children}</Text>
}

TaskCardDescription.displayName = 'TaskCard.Description'

function TaskCardFooter({ children }: TaskCardSlotProps): JSX.Element {
  return <div className={styles.footer}>{children}</div>
}

TaskCardFooter.displayName = 'TaskCard.Footer'

const TaskCardAction = forwardRef<HTMLButtonElement, TaskCardActionProps>(
  function TaskCardAction(
    { className, type = 'button', variant = 'primary', ...props },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={clsx(
          styles.action,
          variant === 'tertiary' && styles.actionTertiary,
          className,
        )}
        {...props}
      />
    )
  },
)

TaskCardAction.displayName = 'TaskCard.Action'

type TaskCardPrimaryActionProps = ComponentPropsWithoutRef<typeof Button>

const TaskCardPrimaryAction = forwardRef<
  HTMLButtonElement,
  TaskCardPrimaryActionProps
>(function TaskCardPrimaryAction({ className, ...props }, ref) {
  const { status } = useTaskCardContext('TaskCard.PrimaryAction')

  if (status === BUFFER_HOME_TASK_STATUS.Completed) {
    return null
  }

  return (
    <Button
      ref={ref}
      variant="secondary"
      size="small"
      className={clsx(styles.primaryAction, className)}
      {...props}
    />
  )
})

TaskCardPrimaryAction.displayName = 'TaskCard.PrimaryAction'

type TaskCardCompletedLabelProps = {
  children: ReactNode
  className?: string
}

const TaskCardCompletedLabel = forwardRef<
  HTMLSpanElement,
  TaskCardCompletedLabelProps
>(function TaskCardCompletedLabel({ children, className }, ref) {
  const { status } = useTaskCardContext('TaskCard.CompletedLabel')

  if (status !== BUFFER_HOME_TASK_STATUS.Completed) {
    return null
  }

  return (
    <span ref={ref} className={clsx(styles.completedLabel, className)}>
      {children}
    </span>
  )
})

TaskCardCompletedLabel.displayName = 'TaskCard.CompletedLabel'

type TaskCardCompleteActionProps = Omit<
  ComponentPropsWithRef<'button'>,
  'children'
> & {
  /** Tooltip copy shown when the button is disabled (e.g. "Connect a channel
   * to mark this done"). When omitted, the disabled button shows no tooltip. */
  disabledReason?: ReactNode
}

const TaskCardCompleteAction = forwardRef<
  HTMLButtonElement,
  TaskCardCompleteActionProps
>(function TaskCardCompleteAction(
  {
    className,
    type = 'button',
    disabled,
    disabledReason,
    'aria-label': ariaLabel,
    ...props
  },
  ref,
) {
  const { status } = useTaskCardContext('TaskCard.CompleteAction')

  if (status === BUFFER_HOME_TASK_STATUS.Dismissed) {
    return null
  }

  const done = status === BUFFER_HOME_TASK_STATUS.Completed
  const isDisabled = disabled || done
  const label = ariaLabel ?? (done ? 'Task completed' : 'Mark as done')
  const tooltipContent = done
    ? 'Task completed'
    : disabled
      ? disabledReason
      : 'Mark as done'

  const button = (
    <IconButton
      ref={ref}
      type={type}
      label={label}
      variant="tertiary"
      size="small"
      aria-pressed={done}
      disabled={isDisabled}
      className={clsx(done && styles.completeActionDone, className)}
      {...props}
    >
      <CircleCheckBigIcon />
    </IconButton>
  )

  if (!tooltipContent) {
    return <span className={styles.completeActionWrapper}>{button}</span>
  }

  // Wrap in an extra span so the Tooltip hover area persists even when the
  // button is disabled (disabled <button> elements don't fire pointer events).
  return (
    <Tooltip content={tooltipContent}>
      <span className={styles.completeActionWrapper}>{button}</span>
    </Tooltip>
  )
})

TaskCardCompleteAction.displayName = 'TaskCard.CompleteAction'

export const TaskCard = Object.assign(TaskCardRoot, {
  Header: TaskCardHeader,
  Icon: TaskCardIcon,
  Title: TaskCardTitle,
  Description: TaskCardDescription,
  Footer: TaskCardFooter,
  Action: TaskCardAction,
  PrimaryAction: TaskCardPrimaryAction,
  CompleteAction: TaskCardCompleteAction,
  CompletedLabel: TaskCardCompletedLabel,
})
